import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Explanation } from '@pen/shared';
import { deckRenderer, diagramRenderer, type VisualHandle } from './renderers';
import { VideoExplanationView } from './VideoExplanationView';
import './explain.css';

export function ExplanationView({ content }: { content: Explanation }) {
  return content.kind === 'video' ? <VideoExplanationView content={content} /> : <VisualExplanationView content={content} />;
}

function VisualExplanationView({ content }: { content: Exclude<Explanation, { kind: 'video' }> }) {
  const host = useRef<HTMLDivElement>(null), handle = useRef<VisualHandle | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [index, setIndex] = useState(0), [expanded, setExpanded] = useState(false), [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    try { handle.current = content.kind === 'diagram' ? diagramRenderer.mount(host.current!, content) : deckRenderer.mount(host.current!, content); }
    catch { setError('This picture could not be displayed. You can still ask the tutor to explain it in words.'); }
    return () => { handle.current?.destroy();handle.current = null; };
  }, [content, expanded]);
  useEffect(() => { setIndex(0); }, [content]);
  useEffect(() => { if (expanded) dialog.current?.showModal();handle.current?.go(index); }, [expanded]);
  useEffect(() => {
    if (!expanded) return;
    const close = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', close);return () => document.removeEventListener('keydown', close);
  }, [expanded]);
  const count = content.kind === 'deck' ? content.steps.length : 1;
  function go(n: number) { const next = Math.max(0, Math.min(count - 1, n));handle.current?.go(next);setIndex(next); }
  const figure = <figure className="explanation-view" aria-label={content.title}>
    <header><strong>{content.title}</strong><button type="button" className="secondary" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? 'Close' : 'Expand'}</button></header>
    {error && <p role="alert">{error}</p>}<div className="explanation-stage" ref={host} />
    {content.kind === 'deck' && <><figcaption aria-live="polite">{content.steps[index]?.caption}</figcaption><nav aria-label="Explanation steps"><button type="button" disabled={index === 0} onClick={() => go(index - 1)}>← Back</button><span>{index + 1} / {count}</span><button type="button" disabled={index === count - 1} onClick={() => go(index + 1)}>Next →</button></nav></>}
  </figure>;
  return expanded ? createPortal(<dialog ref={dialog} className="explanation-dialog" aria-label={content.title} onCancel={() => setExpanded(false)}>{figure}</dialog>, document.body) : figure;
}
